import { useState } from "react";

import { faCircleCheck, faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Footer from "../components/Footer/Footer";
import Header from "../components/Header/Header";
import SEOHead from "../components/SEOHead/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";
import { useToast } from "../contexts/ToastContext";
import { apiService } from "../services/apiService";

import "./Contact.css";

const Contact = () => {
  const { t, translations } = useLanguage();
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: translations?.seo?.contact?.title || "Contact Snappin",
    description:
      translations?.seo?.contact?.description ||
      "Contact Snappin for support, questions, or feedback. Snappin is the easiest way to collect and share event photos.",
    url: `${window.location.origin}/contact`,
    mainEntity: {
      "@type": "Organization",
      name: "Snappin",
      description:
        "Snappin is the easiest way to collect, share, and manage event photos. Create your event with Snappin and let your guests upload their photos instantly.",
      url: window.location.origin,
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "Customer Service",
        availableLanguage: ["English", "Spanish"],
      },
    },
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (
        !formData.name.trim() ||
        !formData.email.trim() ||
        !formData.subject.trim() ||
        !formData.message.trim()
      ) {
        throw new Error(t("contact.form.errorMessage"));
      }

      // Submit the form using the API service
      const response = await apiService.submitContactForm({
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject,
        message: formData.message.trim(),
      });

      if (response.success) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", subject: "", message: "" });
        showSuccess(t("contact.form.successMessage"));
      } else {
        throw new Error(response.message || t("contact.form.errorMessage"));
      }
    } catch (error) {
      setSubmitStatus("error");

      // Show appropriate error message
      const errorMessage =
        error.data?.message || error.message || t("contact.form.errorMessage");
      showError(errorMessage);

      // If there are validation errors, show them
      if (error.data?.errors && Array.isArray(error.data.errors)) {
        error.data.errors.forEach((err) => {
          showError(err);
        });
      }
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };

  return (
    <div className="contact-page">
      <SEOHead
        title={translations?.seo?.contact?.title}
        description={translations?.seo?.contact?.description}
        type="website"
        schema={schema}
      />
      <Header />
      <div className="contact">
        {/* Hero Section */}
        <section className="contact-hero">
          <div className="container">
            <div className="contact-hero-content">
              <h1>{t("contact.title")}</h1>
              <p className="hero-subtitle">{t("contact.subtitle")}</p>
            </div>
          </div>
        </section>

        {/* Contact Content */}
        <section className="contact-content">
          <div className="container">
            <div className="contact-grid">
              {/* Contact Info */}
              <div className="contact-info">
                <h2>{t("contact.info.title")}</h2>
                <p>{t("contact.info.description")}</p>
              </div>

              {/* Contact Form */}
              <div className="contact-form-container">
                <div className="contact-form-card">
                  <h2>{t("contact.form.title")}</h2>
                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">{t("contact.form.name")}</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder={t("contact.form.name")}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">{t("contact.form.email")}</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="email@example.com"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="subject">
                        {t("contact.form.subject")}
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">
                          {t("contact.form.selectSubject")}
                        </option>
                        <option value="general">
                          {t("contact.form.subjectsOptions.general")}
                        </option>
                        <option value="support">
                          {t("contact.form.subjectsOptions.support")}
                        </option>
                        <option value="bug">
                          {t("contact.form.subjectsOptions.bug")}
                        </option>
                        <option value="other">
                          {t("contact.form.subjectsOptions.other")}
                        </option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">
                        {t("contact.form.message")}
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows="6"
                        placeholder={t("contact.form.messagePlaceholder")}
                      ></textarea>
                    </div>

                    {submitStatus === "success" && (
                      <div className="submit-status success">
                        <FontAwesomeIcon icon={faCircleCheck} /> {t("contact.form.successMessage")}
                      </div>
                    )}

                    {submitStatus === "error" && (
                      <div className="submit-status error">
                        <FontAwesomeIcon icon={faCircleExclamation} /> {t("contact.form.errorMessage")}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary btn-large"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? t("contact.form.sending")
                        : t("contact.form.sendMessage")}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
